'use client';

import { useMemo, useRef, type FC } from 'react';
import type { ToolCallMessagePartComponent } from '@assistant-ui/react';
import ReactEChartsCore from 'echarts-for-react/lib/core';
import * as echarts from 'echarts/core';
import { BarChart, LineChart, PieChart } from 'echarts/charts';
import {
  GridComponent,
  LegendComponent,
  TooltipComponent
} from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';
import type { EChartsOption } from 'echarts';
import { DownloadIcon, Loader2Icon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { getColor } from '@/helpers/utils';
import { useAppContext } from '@/Main';
import { TooltipIconButton } from '@/components/assistant-ui/tooltip-icon-button';

echarts.use([
  BarChart,
  LineChart,
  PieChart,
  GridComponent,
  LegendComponent,
  TooltipComponent,
  CanvasRenderer
]);

export type PresentChartArgs = {
  title?: string;
  type?: 'bar' | 'line' | 'pie' | 'area' | 'rings';
  data?: Array<Record<string, string | number>>;
  xKey?: string;
  dataKeys?: string[];
} & Record<string, unknown>;

const SERIES_FALLBACK_COLORS = [
  '#00d27a',
  '#f5803e',
  '#27bcfd',
  '#ff3366',
  '#748194'
];

/** Panel body font (`$font-family-base` / Dana). */
const CHART_FONT_FAMILY = 'dana';

const chartTextStyle = {
  fontFamily: CHART_FONT_FAMILY
};

function seriesColors(): string[] {
  // Resolved theme primary (user-selected Falcon color), not a CSS var —
  // ECharts canvas cannot paint `var(--falcon-primary)`.
  const primary = getColor('primary') || '#2c7be5';
  return [primary, ...SERIES_FALLBACK_COLORS];
}

const PREFERRED_LABEL_KEYS = ['keyword', 'title', 'label', 'name'];

/** Number from a cell; accepts Persian/Arabic digits and thousand separators. */
function cellNumber(value: unknown): number | null {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value !== 'string') return null;
  const normalized = value
    .trim()
    .replace(/[۰-۹]/g, d => String(d.charCodeAt(0) - 0x06f0))
    .replace(/[٠-٩]/g, d => String(d.charCodeAt(0) - 0x0660))
    .replace(/[,٬\s]/g, '')
    .replace('٫', '.');
  if (!normalized || !/^-?\d+(\.\d+)?$/.test(normalized)) return null;
  return Number(normalized);
}

function cellLabel(value: unknown): string {
  if (typeof value === 'string') return value.trim();
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  return '';
}

/**
 * Match xKey/dataKeys to the keys the rows actually carry (models sometimes
 * name keys their rows don't have, which draws empty axes). Mirrors the
 * server-side `repairPresentChartArgs`; null when nothing is plottable.
 */
function normalizeChartArgs(args: PresentChartArgs): PresentChartArgs | null {
  const rows = (Array.isArray(args.data) ? args.data : []).filter(
    row => row && typeof row === 'object' && !Array.isArray(row)
  );
  if (!rows.length) return null;

  const keys = [...new Set(rows.flatMap(row => Object.keys(row)))];
  const hasLabel = (key: string) => rows.some(row => cellLabel(row[key]));
  const hasNumber = (key: string) =>
    rows.some(row => cellNumber(row[key]) !== null);
  const isTextKey = (key: string) =>
    rows.some(row => cellLabel(row[key]) && cellNumber(row[key]) === null);

  const xKey =
    args.xKey && hasLabel(args.xKey)
      ? args.xKey
      : (PREFERRED_LABEL_KEYS.find(
          key => keys.includes(key) && hasLabel(key)
        ) ?? keys.find(isTextKey));
  if (!xKey) return null;

  let dataKeys = (args.dataKeys ?? []).filter(
    key => key !== xKey && hasNumber(key)
  );
  if (!dataKeys.length) {
    dataKeys = keys.filter(key => key !== xKey && hasNumber(key));
  }
  if (!dataKeys.length) return null;

  const data = rows
    .filter(row => cellLabel(row[xKey]))
    .map(row => {
      const out: Record<string, string | number> = {
        [xKey]: cellLabel(row[xKey])
      };
      for (const key of dataKeys) out[key] = cellNumber(row[key]) ?? 0;
      return out;
    });
  if (!data.length) return null;

  return { ...args, data, xKey, dataKeys };
}

function buildOption(args: PresentChartArgs): EChartsOption | null {
  const { type = 'bar', data, xKey, dataKeys } = args;
  if (!data?.length || !xKey || !dataKeys?.length) return null;

  const categories = data.map(row => String(row[xKey] ?? ''));
  const colors = seriesColors();

  if (type === 'pie') {
    const key = dataKeys[0];
    return {
      color: colors,
      textStyle: chartTextStyle,
      tooltip: {
        trigger: 'item',
        textStyle: chartTextStyle
      },
      legend: {
        bottom: 0,
        type: 'scroll',
        textStyle: chartTextStyle
      },
      series: [
        {
          type: 'pie',
          radius: ['35%', '65%'],
          avoidLabelOverlap: true,
          itemStyle: { borderRadius: 4 },
          label: {
            formatter: '{b}: {d}%',
            fontFamily: CHART_FONT_FAMILY
          },
          data: data.map(row => ({
            name: String(row[xKey] ?? ''),
            value: Number(row[key] ?? 0)
          }))
        }
      ]
    };
  }

  const isArea = type === 'area';
  const isLine = type === 'line' || isArea;
  const splitLineColor = getColor('gray-300') || '#d8e2ef';
  const axisLabelColor = getColor('gray-600') || '#748194';

  return {
    color: colors,
    textStyle: chartTextStyle,
    tooltip: {
      trigger: 'axis',
      textStyle: chartTextStyle
    },
    legend: {
      data: dataKeys,
      bottom: 0,
      type: 'scroll',
      textStyle: { ...chartTextStyle, color: axisLabelColor }
    },
    grid: {
      left: 12,
      right: 12,
      top: 24,
      bottom: dataKeys.length > 1 ? 48 : 28,
      containLabel: true
    },
    xAxis: {
      type: 'category',
      data: categories,
      axisTick: { alignWithLabel: true, show: false },
      axisLine: { lineStyle: { color: splitLineColor, opacity: 0.5 } },
      axisLabel: {
        fontFamily: CHART_FONT_FAMILY,
        color: axisLabelColor
      },
      splitLine: { show: false }
    },
    yAxis: {
      type: 'value',
      axisLine: { show: false },
      axisTick: { show: false },
      splitLine: {
        show: true,
        lineStyle: {
          type: 'dashed',
          color: splitLineColor,
          opacity: 0.62,
          width: 1
        }
      },
      axisLabel: {
        fontFamily: CHART_FONT_FAMILY,
        color: axisLabelColor
      }
    },
    series: dataKeys.map(key => ({
      name: key,
      type: isLine ? 'line' : 'bar',
      smooth: isLine,
      areaStyle: isArea ? { opacity: 0.18 } : undefined,
      emphasis: { focus: 'series' },
      data: data.map(row => Number(row[key] ?? 0)),
      ...(isLine
        ? {}
        : {
            barMaxWidth: 36,
            itemStyle: { borderRadius: [4, 4, 0, 0] }
          })
    }))
  };
}

const ChartLoading: FC = () => (
  <div
    className="border-border bg-[var(--card)] text-muted-foreground my-2 flex items-center gap-2 rounded-xl border px-4 py-3 text-sm"
    dir="rtl"
  >
    <Loader2Icon className="size-4 shrink-0 animate-spin" aria-hidden />
    در حال آماده‌سازی چارت…
  </div>
);

function sanitizeFileName(name: string): string {
  const cleaned = name
    .trim()
    .replace(/[<>:"/\\|?*\u0000-\u001f]/g, '')
    .replace(/\s+/g, '-')
    .slice(0, 80);
  return cleaned || 'chart-from-Mizfa-Tools';
}

function ringColor(value: number): string {
  if (value >= 90) return '#0cce6b';
  if (value >= 50) return '#ffa400';
  return '#ff4e42';
}

function ScoreRings({ args }: { args: PresentChartArgs }) {
  const key = args.xKey || 'label';
  const valueKey = args.dataKeys?.[0] || 'value';
  const rings = (args.data ?? []).flatMap(row => {
    const label = cellLabel(row[key]);
    const value = cellNumber(row[valueKey]);
    if (!label || value === null) return [];
    return [{ label, value: Math.max(0, Math.min(100, Math.round(value))) }];
  });
  if (!rings.length) return null;
  const radius = 36;
  const circumference = 2 * Math.PI * radius;

  return (
    <div
      className="border-border bg-[var(--card)] my-2 rounded-xl border px-3 py-4"
      dir="ltr"
    >
      {args.title ? (
        <h3
          className="text-foreground mb-3 text-center text-sm font-semibold"
          dir="rtl"
        >
          {args.title}
        </h3>
      ) : null}
      <div className="flex flex-wrap items-start justify-center gap-4">
        {rings.map(ring => {
          const dash = (ring.value / 100) * circumference;
          return (
            <div
              key={ring.label}
              className="flex w-[92px] flex-col items-center gap-1"
            >
              <svg viewBox="0 0 100 100" className="h-[88px] w-[88px]">
                <circle
                  cx="50"
                  cy="50"
                  r={radius}
                  fill="none"
                  stroke="#445066"
                  strokeWidth="8"
                />
                <circle
                  cx="50"
                  cy="50"
                  r={radius}
                  fill="none"
                  stroke={ringColor(ring.value)}
                  strokeWidth="8"
                  strokeLinecap="round"
                  strokeDasharray={`${dash} ${circumference - dash}`}
                  transform="rotate(-90 50 50)"
                />
                <text
                  x="50"
                  y="56"
                  textAnchor="middle"
                  fill="currentColor"
                  fontSize="18"
                  fontFamily="dana"
                >
                  {ring.value}%
                </text>
              </svg>
              <span className="text-center text-xs text-teal-400">
                {ring.label}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function EchartsChartCard({ args }: { args: PresentChartArgs }) {
  const chartRef = useRef<ReactEChartsCore | null>(null);
  const {
    config: { isDark }
  } = useAppContext() as { config: { isDark: boolean } };
  const option = useMemo(() => buildOption(args ?? {}), [args]);
  if (!option) return null;

  const downloadChart = () => {
    const dataURL = chartRef.current?.getEchartsInstance()?.getDataURL({
      type: 'png',
      pixelRatio: 2,
      backgroundColor: isDark ? '#121E2D' : '#fff'
    });
    if (!dataURL) return;

    const link = document.createElement('a');
    link.href = dataURL;
    link.download = `${sanitizeFileName(String(args.title ?? ''))}.png`;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div
      className={cn(
        'border-border bg-[var(--card)] my-2 overflow-hidden rounded-xl border px-3 pt-3 pb-2'
      )}
      dir="ltr"
    >
      <div className="mb-1 flex items-center gap-1 px-1" dir="rtl">
        <div className="min-w-0 flex-1">
          {args.title ? (
            <h3 className="text-foreground text-center text-sm font-semibold">
              {args.title}
            </h3>
          ) : null}
        </div>
        <TooltipIconButton
          tooltip="دانلود تصویر"
          side="top"
          variant="ghost"
          className="text-muted-foreground hover:text-foreground size-6 shrink-0"
          onClick={downloadChart}
          aria-label="دانلود تصویر چارت"
        >
          <DownloadIcon className="size-3.5" />
        </TooltipIconButton>
      </div>
      <div className="h-[280px] w-full">
        <ReactEChartsCore
          ref={chartRef}
          echarts={echarts}
          option={option}
          style={{ height: '100%', width: '100%' }}
          opts={{ renderer: 'canvas' }}
          notMerge
          lazyUpdate
        />
      </div>
    </div>
  );
}

export function PresentChartCard({ args }: { args: PresentChartArgs }) {
  if (args?.type === 'rings') return <ScoreRings args={args} />;
  return <PlottableChartCard args={args} />;
}

function PlottableChartCard({ args }: { args: PresentChartArgs }) {
  const normalized = useMemo(() => normalizeChartArgs(args ?? {}), [args]);
  // Nothing plottable: render nothing rather than empty axes.
  if (!normalized) return null;
  return <EchartsChartCard args={normalized} />;
}

export const ChartToolUI: ToolCallMessagePartComponent<
  PresentChartArgs,
  unknown
> = function ChartToolUI({ args, status, isError }) {
  if (status.type === 'running' && !args?.data?.length) {
    return <ChartLoading />;
  }

  if (isError) {
    return (
      <div
        className="border-destructive/40 bg-destructive/10 text-destructive my-2 rounded-xl border px-4 py-3 text-sm"
        dir="rtl"
      >
        نمایش چارت ناموفق بود.
      </div>
    );
  }

  return <PresentChartCard args={args ?? {}} />;
};
